import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import {
  IBuyerSearchBilling,
  IBuyerSearchBillingReq,
  IBuyerSearchBillingRes,
} from '@/interfaces/buyer';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  billingAcceptance,
  billingAcceptances,
  searchBuyerBilling,
} from '@/adapters/buyer';
import { IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import VendorSelector from '@/components/selector/vendor-selector';
import { IVendor } from '@/interfaces/vendor';
import ConfirmApprove from '@/components/modal/confirm-modal';
import RejectModal from '@/components/modal/reject-modal';
import classNames from 'classnames';
import {
  getBuyerBillingStatusColor,
  getBuyerBillingStatusName,
  getBuyerBillingStatusOptions,
} from '@/helpers/buyer';
import { EBuyerBillingStatus } from '@/enums/buyer';
import { jobProcess } from '@/adapters/job';
import { getQueryParam } from '@/utilities/queryUtils';

const { RangePicker } = DatePicker;

const _criteria: IBuyerSearchBillingReq = {
  docNo: '',
  dateFrom: dayjs().add(-1, 'month').startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  pageStart: 1,
  pageSize: -1,
  vendor: null,
  docStatus: EBuyerBillingStatus.PENDING,
};

const BillingPage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<IBuyerSearchBillingReq>({
    ..._criteria,
  });
  const [confirm, setConfirm] = useState<{
    open: boolean;
    item?: IBuyerSearchBilling;
  }>({
    open: false,
  });

  const [reject, setReject] = useState<{
    open: boolean;
    item?: IBuyerSearchBilling;
  }>({
    open: false,
  });

  const [selectedRowKey, setSelectedRowKey] = useState<IBuyerSearchBilling[]>(
    []
  );

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<IBuyerSearchBillingReq>['onFinish'] = (values) => {
    console.log('Success:', values);
    setCriteria({
      ...criteria,
      docNo: values.docNo,
      dateFrom: values.docDate?.[0].format('YYYY-MM-DD') || undefined,
      dateTo: values.docDate?.[1].format('YYYY-MM-DD') || undefined,
      vendor: values.vendor,
      docStatus: values.docStatus,
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    form.submit();
  };

  const columns: TableColumnsType<IBuyerSearchBilling> = [
    {
      title: 'ลำดับ',
      dataIndex: '',
      width: 60,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เอกสารวางบิล',
      dataIndex: 'AR_BILL_NBR',
      align: 'center',
      className: 'text-bold',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'วันที่วางบิล',
      dataIndex: 'AR_BILL_DATE',
      align: 'center',
      className: 'text-bold',
      width: 120,
      render: (_) => formatDate(_),
    },
    {
      title: 'เจ้าหนี้',
      dataIndex: 'AR_VENDER',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ชื่อเจ้าหนี้',
      dataIndex: 'AR_VENDER_NAME',
      className: 'text-bold',
    },
    {
      title: 'เครดิตเทอม',
      dataIndex: 'AR_CR_TERMS',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'AR_AMT_TOTAL',
      align: 'right',
      width: 120,
      className: 'text-bold text-primary',
      render: (_) => formatNumber(_, 2),
    },
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNo',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div>
          {rec.InvoiceList?.map((i) => {
            return <div>{i.AR_NBR}</div>;
          })}
        </div>
      ),
    },
    {
      title: 'วันที่เอกสาร',
      dataIndex: 'invoiceDate',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div>
          {rec.InvoiceList?.map((i) => {
            return <div>{formatDate(i.AR_EFFDATE)}</div>;
          })}
        </div>
      ),
    },
    {
      title: 'วันที่ครบกำหนด',
      dataIndex: 'invoiceDueDate',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div>
          {rec.InvoiceList?.map((i) => {
            return <div>{formatDate(i.AR_DUE_DATE)}</div>;
          })}
        </div>
      ),
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'invoiceAmount',
      align: 'right',
      width: 120,
      render: (_, rec) => (
        <div>
          {rec.InvoiceList?.map((i) => {
            return <div>{formatNumber(i.AR_AMT, 2)}</div>;
          })}
        </div>
      ),
    },
    {
      title: 'สถานะเอกสาร',
      dataIndex: 'AR_STATUS',
      align: 'center',
      width: 120,
      render: (_) => (
        <div className={`${classNames(getBuyerBillingStatusColor(_))} rounded`}>
          {getBuyerBillingStatusName(_)}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: '',
      align: 'center',
      width: 120,
      className: 'text-bold',
      fixed: 'right',
      render: (_, rec) => (
        <div>
          {!['ACPT', 'RJCT'].includes(rec.AR_STATUS) && (
            <Flex gap={6} justify="center">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                style={{ minWidth: 80 }}
                onClick={() => {
                  setReject({
                    open: true,
                    item: rec,
                  });
                }}
              >
                ปธิเสธ
              </button>
            </Flex>
          )}
        </div>
      ),
    },
    {
      title: 'หมายเหตุการปฏิเสธ',
      dataIndex: 'AR_REMARK',
      className: 'text-bold',
    },
  ];

  const { data: billings, refetch } = useQuery<
    AxiosResponse<IBuyerSearchBillingRes>,
    Error
  >({
    queryKey: ['search-buyer-inquiry', criteria],
    queryFn: async () => await searchBuyerBilling(criteria),
    // enabled: !!criteria.vendor,
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info', criteria],
    queryFn: async () => await getServerInfo(),
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IBuyerSearchBilling>) =>
      await billingAcceptance(data),
    mutationKey: ['billing-acceptance'],
    onSuccess: (data) => {
      if (data.status === 200) {
        notification.success({
          message: 'รับเอกสารสำเร็จ',
        });
        refetch();
        setSelectedRowKey([]);
        setConfirm({ open: false, item: undefined });
        setReject({ open: false, item: undefined });
      } else {
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  const mutationGroups = useMutation({
    mutationFn: async (data: Partial<IBuyerSearchBilling>[]) =>
      await billingAcceptances(data),
    mutationKey: ['billing-acceptances'],
    onSuccess: (data) => {
      if (data.status === 200) {
        const response = data?.data;
        if (response?.data?.length) {
          response?.data?.map((c) => {
            return notification.success({
              message: c.refNo,
              description: 'รับเอกสารสำเร็จ',
            });
          });
        } else {
          response?.errors?.map((c) => {
            return notification.error({
              message: c.refNo,
              description: c?.message || c?.errors?.errorMessage,
            });
          });
        }
        setSelectedRowKey([]);
        refetch();
        setConfirm({ open: false, item: undefined });
        setReject({ open: false, item: undefined });
      } else {
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onConfirm(): void {
    const models: IBuyerSearchBilling[] = selectedRowKey.map((c) => {
      return {
        acceptance: 'ACPT',
        ...c,
      };
    });
    mutationGroups.mutateAsync(models);
    // mutations.mutateAsync(a);
  }

  function onReject(error: string): void {
    mutations.mutateAsync({
      acceptance: 'RJCT',
      ...reject.item,
      AR_REMARK: error,
    });
  }

  const rowSelection: TableProps<IBuyerSearchBilling>['rowSelection'] = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: IBuyerSearchBilling[]
    ) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows
      );
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: IBuyerSearchBilling) => ({
      disabled: record.AR_STATUS !== 'OK',
    }),
    selectedRowKeys:
      selectedRowKey?.map((c) => {
        return c.AR_BILL_NBR;
      }) || [],
  };

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('billing') : undefined)}
          >
            ค้นหาเอกสารรับวางบิล
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="docNo" label="เลขที่เอกสาร">
                <Input placeholder="เลขที่เอกสาร" size="large" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<IBuyerSearchBillingReq> name="vendor" label="เจ้าหนี้">
                <VendorSelector
                  handleSelectData={function (vendor: IVendor): void {
                    form.setFieldsValue({
                      vendor: vendor?.VDAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<IBuyerSearchBillingReq> name="docStatus" label="สถานะ">
                <Select
                  showSearch
                  placeholder="เลือกสถานะ"
                  optionFilterProp="label"
                  size="large"
                  defaultValue={criteria?.docStatus}
                  options={[
                    {
                      value: '',
                      label: 'ทั้งหมด',
                    },
                    ...getBuyerBillingStatusOptions(),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <button
              disabled={!selectedRowKey.length || mutations.isPending}
              type="button"
              className="btn btn-success"
              style={{ minWidth: 150 }}
              onClick={() => onConfirm()}
            >
              {mutations.isPending || mutationGroups.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'รับเอกสาร '
              )}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ minWidth: 150 }}
            >
              ค้นหา
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ minWidth: 150 }}
              onClick={onClear}
            >
              ยกเลิก
            </button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<IBuyerSearchBilling>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={billings?.data?.data || []}
          size="small"
          bordered
          rowKey="AR_BILL_NBR"
          scroll={{ x: 1800 }}
          pagination={false}
        />
      </Card>
      <ConfirmApprove
        open={confirm.open}
        title={'ยืนการส่งเอกสาร'}
        content={'ยืนยันการส่งเอกสาร'}
        handleOk={function (): void {
          onConfirm();
        }}
        handleCancel={function (): void {
          setConfirm({
            open: false,
            item: undefined,
          });
        }}
      />
      <RejectModal
        open={reject.open}
        title={'ยืนการส่งเอกสาร'}
        content={'ปฏิเสธการส่งเอกสาร'}
        handleOk={function (err): void {
          onReject(err);
        }}
        handleCancel={function (): void {
          setReject({
            open: false,
            item: undefined,
          });
        }}
      />
    </>
  );
};

export default BillingPage;
